import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Crown, Check, Zap, ArrowRight, Shield, Star,
  Sparkles, Brain, FileText, Users, BarChart3, Rocket
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { subscriptionAPI } from '../lib/api';

const TIER_ICONS = { free: Zap, pro: Crown, enterprise: Rocket };
const TIER_COLORS = {
  free: { accent: '#A0AEC0', bg: 'rgba(160,174,192,0.08)', border: 'rgba(160,174,192,0.15)' },
  pro: { accent: '#0D9488', bg: 'rgba(13,148,136,0.08)', border: 'rgba(13,148,136,0.2)' },
  enterprise: { accent: '#805AD5', bg: 'rgba(128,90,213,0.08)', border: 'rgba(128,90,213,0.2)' },
};
const FEATURE_ICONS = [Brain, FileText, Users, BarChart3, Shield, Star, Sparkles, Rocket];

export default function SubscriptionPage() {
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const load = async () => {
      try {
        const [plansRes, currentRes] = await Promise.all([
          subscriptionAPI.plans(),
          subscriptionAPI.current(),
        ]);
        setPlans(plansRes.data);
        setCurrentPlan(currentRes.data);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load subscription data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Auto-trigger checkout if ?upgrade=plan param is present (from register flow)
  useEffect(() => {
    const upgradePlan = searchParams.get('upgrade');
    if (upgradePlan && !loading && plans.length > 0 && currentPlan) {
      if (currentPlan.plan_id !== upgradePlan && (upgradePlan === 'pro' || upgradePlan === 'enterprise')) {
        handleCheckout(upgradePlan);
      }
    }
  }, [searchParams, loading, plans, currentPlan]);

  const handleCheckout = async (planId) => {
    if (planId === 'free') return;
    setCheckoutLoading(planId);
    try {
      const res = await subscriptionAPI.checkout(planId, window.location.origin);
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: '#0D9488', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="subscription-page">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <p className="nx-overline mb-2" style={{ color: '#0D9488' }}>Subscription</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-none" style={{ color: 'var(--t-text-heading)' }}>
          Choose Your Plan
        </h1>
        <p className="mt-3 text-sm sm:text-base" style={{ color: 'var(--t-text-muted)' }}>
          Unlock premium features to accelerate your career growth
        </p>
      </div>

      {/* Current Plan Badge */}
      {currentPlan && currentPlan.plan_id !== 'free' && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
            data-testid="current-plan-badge"
            style={{ background: TIER_COLORS[currentPlan.plan_id]?.bg, border: `1px solid ${TIER_COLORS[currentPlan.plan_id]?.border}`, color: TIER_COLORS[currentPlan.plan_id]?.accent }}>
            <Crown className="w-4 h-4" />
            Active: {currentPlan.plan_name}
            {currentPlan.expires_at && (
              <span className="text-xs opacity-70 ml-1">
                (expires {new Date(currentPlan.expires_at).toLocaleDateString()})
              </span>
            )}
          </div>
        </motion.div>
      )}

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {plans.map((plan, idx) => {
          const colors = TIER_COLORS[plan.id] || TIER_COLORS.free;
          const Icon = TIER_ICONS[plan.id] || Zap;
          const isActive = currentPlan?.plan_id === plan.id;
          const isPopular = plan.id === 'pro';

          return (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              data-testid={`plan-card-${plan.id}`}
              className="relative nx-card p-6 flex flex-col"
              style={{
                borderColor: isPopular ? colors.accent : 'var(--t-border)',
                borderWidth: isPopular ? '2px' : '1px',
              }}
            >
              {/* Popular badge */}
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                    data-testid="popular-badge"
                    style={{ background: '#0D9488', color: '#fff', border: 'none' }}>
                    Most Popular
                  </Badge>
                </div>
              )}

              {/* Plan header */}
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center"
                  style={{ background: colors.bg }}>
                  <Icon className="w-6 h-6" style={{ color: colors.accent }} />
                </div>
                <h3 className="text-xl font-extrabold" style={{ color: 'var(--t-text-heading)' }}>{plan.name}</h3>
                <p className="text-xs mt-1" style={{ color: 'var(--t-text-muted)' }}>{plan.description}</p>
                <div className="mt-4 flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-extrabold" style={{ color: 'var(--t-text-heading)' }}>
                    {plan.price === 0 ? 'Free' : `$${plan.price}`}
                  </span>
                  {plan.interval && (
                    <span className="text-sm" style={{ color: 'var(--t-text-muted)' }}>/{plan.interval}</span>
                  )}
                </div>
              </div>

              {/* Features */}
              <div className="flex-1 space-y-3 mb-6">
                {plan.features.map((feature, fi) => {
                  const FIcon = FEATURE_ICONS[fi % FEATURE_ICONS.length];
                  return (
                    <div key={fi} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: colors.bg }}>
                        <Check className="w-3 h-3" style={{ color: colors.accent }} />
                      </div>
                      <span className="text-sm" style={{ color: 'var(--t-text-secondary)' }}>{feature}</span>
                    </div>
                  );
                })}
              </div>

              {/* CTA */}
              <Button
                data-testid={`plan-cta-${plan.id}`}
                onClick={() => handleCheckout(plan.id)}
                disabled={isActive || plan.id === 'free' || checkoutLoading === plan.id}
                className="w-full h-11 font-bold text-sm uppercase tracking-wider rounded-lg transition-all"
                style={{
                  background: isActive ? 'var(--t-bg-section)' : plan.id === 'free' ? 'var(--t-bg-section)' : colors.accent,
                  color: isActive || plan.id === 'free' ? 'var(--t-text-muted)' : '#fff',
                  border: `1px solid ${isActive || plan.id === 'free' ? 'var(--t-border)' : colors.accent}`,
                  opacity: isActive ? 0.6 : 1,
                }}
              >
                {checkoutLoading === plan.id ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : isActive ? (
                  'Current Plan'
                ) : plan.id === 'free' ? (
                  'Free Forever'
                ) : (
                  <span className="flex items-center gap-2">
                    Upgrade to {plan.name} <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </motion.div>
          );
        })}
      </div>

      {/* FAQ / Trust */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
        className="max-w-2xl mx-auto text-center pt-4" data-testid="subscription-footer">
        <div className="flex items-center justify-center gap-6 mb-4">
          {[
            { icon: Shield, text: 'Secure payments' },
            { icon: Zap, text: 'Instant activation' },
            { icon: Star, text: 'Cancel anytime' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--t-text-muted)' }}>
              <item.icon className="w-3.5 h-3.5" style={{ color: '#0D9488' }} />
              {item.text}
            </div>
          ))}
        </div>
        <p className="text-xs" style={{ color: 'var(--t-text-muted)', opacity: 0.6 }}>
          Powered by Stripe. Your payment information is encrypted and secure.
        </p>
      </motion.div>
    </div>
  );
}
